/**
 * Central Design System - Main Export
 * 
 * This file exports all styling definitions from the centralized design system.
 * Import from this file to access all styles, or import specific modules for better tree-shaking.
 * 
 * Usage: 
 * import { styles, tokens, cx } from '@/styles';
 * import { chartConfigs } from '@/styles/charts';
 * import { headerStyles } from '@/styles/components';
 * import { motionVariants } from '@/styles/animations';
 */

// Re-export all style modules
export { default as componentStyles, headerStyles, searchStyles, dateRangeStyles, cardStyles, shiftStyles, tabStyles, viewModeStyles } from './components';
export { chartConfigs, chartStyles, getDefaultColor, mergeChartConfig } from './charts';
export { default as animationStyles, motionVariants, cssAnimations, springConfigs, easingFunctions, animationUtils, componentAnimations, createStaggerAnimation, createFadeTransition, createSlideTransition } from './animations';

// Re-export theme system
export { themes, getTheme, getAvailableThemes, generateCSSVariables, lightTheme, darkTheme, highContrastTheme } from './themes';
export type { Theme, ThemeColors, ThemeName } from './themes';

// Re-export types
export type * from './types';

// =============================================================================
// THEME-AWARE STYLES PROVIDER
// =============================================================================

/**
 * Generate theme-aware styles using the theme classes
 * This replaces the hardcoded static styles object
 */
export const createThemeAwareStyles = (themeClasses: any) => ({
  // Button styles
  button: {
    // Base button styles
    base: 'inline-flex items-center justify-center font-medium focus:outline-none transition-all duration-300 ease-out transform hover:scale-[1.02]',
    
    // Button variants - using theme-aware classes
    primary: `bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-400/30 shadow-lg shadow-blue-500/25 ring-2 ring-blue-400/20 hover:scale-[1.02] hover:-translate-y-0.5`,
    
    secondary: `${themeClasses.bgSecondary} ${themeClasses.borderPrimary} ${themeClasses.textSecondary} ${themeClasses.hover} border transition-all duration-300`,
    
    ghost: `bg-transparent text-white hover:bg-white/10 transition-colors`,
    
    // Themed variants for dashboard options
    success: `bg-gradient-to-br from-green-500 to-green-600 text-white border-green-400/30 shadow-lg shadow-green-500/25 ring-2 ring-green-400/20 hover:scale-[1.02] hover:-translate-y-0.5`,
    
    warning: `bg-gradient-to-br from-yellow-400 to-yellow-500 text-gray-900 border-yellow-300/30 shadow-lg shadow-yellow-400/25 ring-2 ring-yellow-300/20 hover:scale-[1.02] hover:-translate-y-0.5`,
    
    accent: `bg-gradient-to-br from-purple-500 to-purple-600 text-white border-purple-400/30 shadow-lg shadow-purple-500/25 ring-2 ring-purple-400/20 hover:scale-[1.02] hover:-translate-y-0.5`,
    
    // Button sizes
    small: 'px-3 py-1.5 text-sm rounded-md',
    medium: 'px-4 py-2 text-sm rounded-md',
    large: 'px-6 py-3 text-base rounded-lg',
    
    // Special button styles
    tab: `group relative px-4 lg:px-6 py-3 lg:py-4 rounded-xl font-medium text-sm lg:text-base transition-all duration-300 ease-out transform hover:scale-[1.02] hover:-translate-y-0.5 backdrop-blur-sm border overflow-hidden`,
    
    servicePortal: `bg-gradient-to-r from-emerald-500 to-teal-500 dark:from-emerald-400 dark:to-teal-400 text-white font-bold px-6 py-3 rounded-full shadow-lg hover:shadow-2xl hover:from-emerald-600 hover:to-teal-600 dark:hover:from-emerald-500 dark:hover:to-teal-500 text-sm border border-emerald-400/30 backdrop-blur-sm transition-all duration-300 ease-out hover:scale-110 active:scale-95 transform`,
    
    hamburger: `flex items-center gap-2 text-sm text-white focus:outline-none`,
  },
  
  // Card styles
  card: {
    base: `rounded-lg shadow-md border relative transform-gpu transition-transform duration-300 ${themeClasses.bgSurface} ${themeClasses.borderPrimary}`,
    
    interactive: `hover:shadow-lg transition-shadow cursor-pointer hover:-rotate-1 hover:scale-105 active:rotate-0.5 active:scale-100`,
    
    machine: `rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer border relative transform-gpu transition-transform duration-300 hover:-rotate-1 hover:scale-105 active:rotate-0.5 active:scale-100 ${themeClasses.bgSurface} ${themeClasses.borderPrimary}`,
    
    line: `rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer border relative transform-gpu transition-transform duration-300 hover:-rotate-1 hover:scale-105 active:rotate-0.5 active:scale-100 ${themeClasses.bgSurface} ${themeClasses.borderPrimary}`,
    
    chart: `sm:rounded-xl px-1 pb-2 sm:p-4 lg:p-6`,
  },
  
  // Header styles
  header: {
    base: `sticky top-0 z-50 shadow-lg rounded-b-[2.5rem] px-10 flex flex-col overflow-hidden`,
    
    gradient: `linear-gradient(287.56deg, #037AC7 27.21%, #84D1F1 43.79%, #037AC7 61.44%)`,
    
    topBar: `flex items-center justify-between relative w-full`,
    
    machineCard: `relative flex flex-col md:flex-row items-center justify-center transition-all duration-300 ease-out origin-top`,
    
    lineCard: `relative flex flex-col md:flex-row items-center justify-center transition-all duration-300 ease-out origin-top`,
    
    scrolled: {
      machineCard: `scale-100 opacity-100 min-h-[50px] py-1`,
      lineCard: `scale-100 opacity-100 min-h-[40px] py-0.5`,
    },
    
    normal: {
      machineCard: `scale-100 opacity-100 min-h-[140px] py-6`,
      lineCard: `scale-100 opacity-100 min-h-[140px] py-6`,
    },
    
    languageSelector: {
      option: 'bg-gray-800 text-white',
    },
  },
  
  // Navigation styles
  navigation: {
    // Above the date-range modal (`app.modal.overlay`, z-[9999]): the sidebar is
    // top-level navigation, so opening it must not leave the picker floating on
    // top of it. Its click-catcher overlay sits just below the drawer.
    sidebar: `fixed top-0 left-0 h-full w-80 bg-sidebar z-[10001] shadow-2xl flex flex-col transition-transform duration-300`,
    
    sidebarOpen: `translate-x-0`,
    sidebarClosed: `-translate-x-full`,
    
    overlay: `fixed inset-0 z-[10000]`,
    
    hamburgerIcon: `relative w-8 h-8 flex items-center justify-center`,
    hamburgerLine: `absolute h-0.5 w-6 bg-white rounded transition-all duration-300`,
  },
  
  // Status indicator styles
  status: {
    base: `w-3 h-3 rounded-full flex-shrink-0`,
    success: `w-3 h-3 rounded-full flex-shrink-0 bg-green-500`,
    error: `w-3 h-3 rounded-full flex-shrink-0 bg-red-500`,
    warning: `w-3 h-3 rounded-full flex-shrink-0 bg-yellow-500`,
    disabled: `w-3 h-3 rounded-full flex-shrink-0 bg-gray-400`,
    info: `w-3 h-3 rounded-full flex-shrink-0 bg-blue-500`,
    
    // Connection status
    connection: {
      connected: `w-3 h-3 rounded-full bg-green-400`,
      disconnected: `w-3 h-3 rounded-full bg-red-400`,
    },
  },
  
  // Input styles
  input: {
    base: `block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500`,
    
    search: {
      container: `relative flex items-center`,
      button: `rounded-full flex items-center justify-center transition-all duration-300 ease-out transform-gpu`,
      expanded: `w-12 h-12 p-2 hover:bg-cockpitblue-light/30 text-white`,
      collapsed: `w-12 h-12 p-2 hover:bg-cockpitblue-light/30 text-white`,
      input: `flex-1 bg-transparent text-white placeholder-white/70 focus:outline-none focus:ring-0 border-0 text-base`,
    },
    
    dateRange: `backdrop-blur-sm text-white font-medium rounded-lg px-6 py-3 transition-all duration-300 ease-out cursor-pointer text-base text-center focus:outline-none focus:ring-0`,
    
    shift: {
      container: `flex flex-col gap-2`,
      buttonGroup: `flex gap-2 bg-white/80 rounded-lg px-2 py-1 border border-blue-200`,
      button: `px-3 py-1 rounded font-bold border text-xs font-mono transition-all`,
      selected: `bg-blue-600 text-white border-blue-700 shadow`,
      unselected: `bg-white text-blue-700 border-blue-200 hover:bg-blue-50`,
    },
  },
  
  // Tab styles
  tabs: {
    container: `grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-4 mb-6`,
    
    tab: `group relative px-4 lg:px-6 py-3 lg:py-4 rounded-xl font-medium text-sm lg:text-base transition-all duration-300 ease-out transform hover:scale-[1.02] hover:-translate-y-0.5 backdrop-blur-sm border overflow-hidden`,
    
    tabActive: `bg-gradient-to-br from-blue-500 to-blue-600 text-white border-blue-400/30 shadow-lg shadow-blue-500/25 ring-2 ring-blue-400/20`,
    
    tabInactive: `theme-bg-surface/60 theme-border-secondary/50 theme-text-primary hover:theme-bg-surface/80 hover:theme-border-secondary/60 hover:theme-text-primary hover:shadow-md`, // replaced bg-white/60 border-gray-200/50 text-gray-700 etc
    
    // Sub-tabs (flat style with underline)
    subTabs: {
      container: `flex border-b theme-border-primary`, // replaced border-gray-200
      tab: `px-6 py-3 text-sm font-medium border-b-2 transition-colors`,
      active: `theme-text-accent theme-border-accent`, // replaced text-blue-600 border-blue-600
      inactive: `theme-text-secondary border-transparent hover:theme-text-primary hover:theme-border-secondary`, // replaced text-gray-500 hover:text-gray-700 hover:border-gray-300
    },
    
    // Chrome-style tabs
    chromeTabs: {
      container: `flex theme-bg-tertiary border-b theme-border-tertiary relative`, // replaced bg-slate-200 border-slate-300
      tab: `px-4 py-2 border theme-border-tertiary border-b-0 rounded-t-lg mr-1 min-w-[100px] max-w-[200px] transition-colors duration-150 flex items-center justify-center relative`, // replaced border-slate-300
      active: `theme-bg-surface theme-text-primary shadow-md theme-border-tertiary`, // replaced bg-white text-slate-800 border-slate-300
      inactive: `theme-bg-tertiary theme-text-secondary theme-border-tertiary hover:theme-bg-hover`, // replaced bg-slate-100 text-slate-500
      closeButton: `ml-2 w-4 h-4 flex items-center justify-center rounded hover:theme-bg-hover theme-text-secondary hover:theme-text-primary border border-transparent`, // replaced text-slate-400 hover:text-slate-700
      content: {
        wrapper: 'flex items-center justify-center w-full',
        label: 'truncate max-w-[100px]',
      },
      addButton: {
        button: 'ml-2 w-8 h-8 flex items-center justify-center rounded-full hover:theme-bg-hover theme-text-secondary hover:theme-text-primary border border-transparent',
        icon: 'w-5 h-5',
      },
      border: 'absolute left-0 right-0 bottom-0 h-px theme-bg-tertiary z-0', // replaced bg-slate-300
      icon: {
        close: 'w-3 h-3',
        add: 'w-5 h-5',
      },
    },
  },
  
  // All tab styles
  allTab: {
    container: 'space-y-6',
    header: {
      container: 'flex items-center justify-between flex-wrap gap-4',
      title: 'text-lg font-semibold text-title',
      controls: 'flex items-center space-x-4',
      stats: 'text-sm text-gray-600',
      viewModeToggle: {
        container: 'flex bg-gray-100 rounded-lg p-1',
        button: {
          base: 'px-4 py-2 rounded-md text-sm font-medium transition-colors',
          active: 'bg-white text-blue-600 shadow-sm',
          inactive: 'text-gray-600 hover:text-gray-900',
        },
      },
    },
    content: {
      treeWrapper: 'bg-white rounded-lg shadow-md p-6',
      globeWrapper: 'bg-white rounded-lg shadow-md p-6',
      cardsContainer: 'space-y-8',
      section: {
        container: 'section',
        title: 'text-md font-semibold text-title mb-4',
        linesGrid: 'grid gap-4 md:grid-cols-2 lg:grid-cols-3',
      },
    },
  },
  
  // Layout styles
  layout: {
    container: `max-w-7xl mx-auto px-4 sm:px-6 lg:px-8`,
    section: `space-y-6`,
    grid: `grid gap-6`,
    gridCols: {
      1: `grid-cols-1`,
      2: `grid-cols-1 md:grid-cols-2`,
      3: `grid-cols-1 md:grid-cols-2 lg:grid-cols-3`,
      4: `grid-cols-1 md:grid-cols-2 lg:grid-cols-4`,
    },
  },
  
  // Typography styles
  typography: {
    heading: {
      h1: `text-3xl font-bold text-gray-900`,
      h2: `text-2xl font-semibold text-gray-900`,
      h3: `text-xl font-semibold text-gray-900`,
      h4: `text-lg font-semibold text-gray-900`,
      h5: `text-base font-semibold text-gray-900`,
    },
    
    body: {
      large: `text-lg text-gray-700`,
      base: `text-base text-gray-700`,
      small: `text-sm text-gray-600`,
      xs: `text-xs text-gray-500`,
    },
    
    label: `text-sm font-medium text-gray-700`,
    caption: `text-xs text-gray-500`,
    
    // Machine/Line info text
    info: {
      title: `font-bold text-white`,
      subtitle: `flex items-center gap-3 text-cockpitblue-light/90 text-xs`,
      compact: `flex items-center gap-4 text-white text-sm transition-all duration-300 ease-out`,
    },
  },
  
  // Animation styles
  animation: {
    fadeIn: `animate-fade-in`,
    slideIn: `animate-slide-in`,
    bounce: `animate-bounce`,
    pulse: `animate-pulse`,
    spin: `animate-spin`,
    
    // Custom animations
    shine: `absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out`,
    
    glow: `absolute inset-0 bg-gradient-to-br from-blue-400/20 to-blue-600/20 rounded-xl blur-sm scale-110`,
    
    // Loading states
    loading: {
      spinner: `animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full`,
      dots: `flex space-x-1`,
      dot: `w-2 h-2 bg-blue-600 rounded-full animate-pulse`,
    },
  },
  
  // Utility styles
  utility: {
    // Flexbox
    flex: {
      center: `flex items-center justify-center`,
      between: `flex items-center justify-between`,
      start: `flex items-center justify-start`,
      end: `flex items-center justify-end`,
      col: `flex flex-col`,
      colCenter: `flex flex-col items-center justify-center`,
    },
    
    // Positioning
    position: {
      absolute: `absolute`,
      relative: `relative`,
      fixed: `fixed`,
      sticky: `sticky`,
      
      // Common absolute positions
      topRight: `absolute top-0 right-0`,
      topLeft: `absolute top-0 left-0`,
      bottomRight: `absolute bottom-0 right-0`,
      bottomLeft: `absolute bottom-0 left-0`,
      center: `absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`,
    },
    
    // Spacing
    spacing: {
      noSpace: `space-y-0`,
      xs: `space-y-1`,
      sm: `space-y-2`,
      md: `space-y-3`,
      lg: `space-y-4`,
      xl: `space-y-6`,
      '2xl': `space-y-8`,
    },
    
    // Visibility
    visibility: {
      visible: `visible opacity-100`,
      hidden: `invisible opacity-0`,
      srOnly: `sr-only`,
    },
    
    // Overflow
    overflow: {
      hidden: `overflow-hidden`,
      auto: `overflow-auto`,
      scroll: `overflow-scroll`,
      visible: `overflow-visible`,
    },
    
    // Pointer events
    pointer: {
      auto: `pointer-events-auto`,
      none: `pointer-events-none`,
    },
    
    // User select
    select: {
      none: `select-none`,
      auto: `select-auto`,
      all: `select-all`,
    },
    
    // Background utilities
    background: {
      gradient: 'bg-gradient-to-br',
      overlay: 'absolute inset-0',
      transparent: 'bg-transparent',
    },
    
    // Text utilities
    text: {
      truncate: 'truncate',
      ellipsis: 'text-ellipsis',
      noWrap: 'whitespace-nowrap',
      wrap: 'whitespace-normal',
    },
    
    // Transform utilities
    transform: {
      gpu: 'transform-gpu',
      none: 'transform-none',
      center: 'transform -translate-x-1/2 -translate-y-1/2',
    },
    
    // Outline utilities
    outline: {
      none: 'outline-none',
      focus: 'focus:outline-none',
    },
  },

  // Machine picture styles
  machinePicture: {
    container: 'w-96 h-56 flex items-center justify-center',
    image: 'max-w-full max-h-full object-contain transition-opacity duration-300',
    loading: {
      container: 'flex flex-col items-center space-y-2',
      spinner: 'animate-spin rounded-full h-6 w-6 border-2 border-white/60 border-t-white',
      text: 'text-white/60 text-sm',
    },
    error: {
      container: 'text-center text-white/80',
      title: 'text-lg font-semibold',
      subtitle: 'text-sm opacity-80 capitalize',
    },
  },

  // Machine icon styles
  machineIcon: {
    container: 'w-8 h-8 flex items-center justify-center rounded-lg transition-colors',
    default: 'bg-gray-100 text-gray-600',
    active: 'bg-blue-100 text-blue-600',
    error: 'bg-red-100 text-red-600',
    success: 'bg-transparent text-gray-600',
    icon: 'w-5 h-5',
  },

  // Line icon styles
  lineIcon: {
    container: 'w-8 h-8 flex items-center justify-center rounded-lg transition-colors',
    default: 'bg-gray-100 text-gray-600',
    active: 'bg-blue-100 text-blue-600',
    icon: 'w-5 h-5',
  },
  
  // NotFound page styles
  notFound: {
    container: 'min-h-screen bg-gray-200 flex flex-col relative overflow-hidden',
    backgroundOverlay: 'absolute inset-0 bg-white/40',
    backgroundImage: 'absolute inset-0 bg-contain bg-center bg-no-repeat',
    logo: {
      container: 'absolute top-8 left-8 z-20',
      image: 'h-8 opacity-80',
    },
    content: {
      container: 'flex-1 flex items-center justify-center px-4',
      wrapper: 'text-center max-w-2xl mx-auto relative z-10',
      title: 'text-2xl md:text-3xl font-semibold text-gray-900 mb-3',
      subtitle: 'text-gray-800 mb-8 text-lg font-medium',
      errorNumber: {
        container: 'relative mb-12',
        text: 'relative text-9xl md:text-[12rem] font-bold text-teal-900 select-none tracking-wide drop-shadow-2xl',
      },
      description: 'text-gray-800 mb-8 text-base leading-relaxed font-medium',
      actions: {
        container: 'flex flex-col sm:flex-row gap-4 justify-center',
        primaryButton: 'bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5',
        secondaryButton: 'border border-gray-300 hover:border-blue-400 text-gray-700 hover:text-blue-600 px-8 py-3 rounded-lg font-medium transition-all duration-200 bg-white hover:bg-blue-50 transform hover:-translate-y-0.5',
      },
    },
  },
  
  // Settings page styles
  settings: {
    container: 'flex flex-col h-full py-8 px-6',
    header: {
      container: 'text-center mb-8',
      title: 'text-4xl font-bold text-title mb-4',
      description: 'text-lg text-gray-500',
    },
    content: {
      wrapper: 'max-w-4xl mx-auto w-full space-y-6',
      section: {
        container: 'bg-white rounded-lg shadow-md p-6',
        title: 'text-xl font-semibold mb-4 text-gray-800',
        content: 'space-y-4',
        item: {
          title: 'font-medium text-gray-900 mb-2',
          description: 'text-sm text-gray-500 mb-3',
          buttonGroup: 'flex gap-3',
          button: {
            primary: 'px-4 py-2 bg-blue-500 text-white rounded-md',
            secondary: 'px-4 py-2 bg-gray-300 text-gray-700 rounded-md',
          },
        },
      },
    },
    developerTools: {
      container: 'flex items-center justify-between',
      header: {
        title: 'font-medium text-gray-900 mb-2',
        description: 'text-sm text-gray-500 mb-3',
      },
      validateButton: {
        base: 'px-4 py-2 rounded-md transition-colors font-medium',
        enabled: 'bg-green-500 hover:bg-green-600 text-white',
        disabled: 'bg-green-500 opacity-50 cursor-not-allowed text-white',
      },
      results: {
        container: 'p-4 bg-gray-50 rounded-md',
        content: 'text-sm space-y-2',
        row: 'flex justify-between',
        label: 'font-medium',
        value: '',
        success: 'text-green-600',
        error: 'text-red-600',
        details: {
          container: 'mt-3',
          summary: 'cursor-pointer text-red-600 font-medium',
          list: 'mt-2 ml-4 text-xs space-y-1',
          item: 'text-red-500',
        },
      },
    },
  },
  
  // Home view styles
  home: {
    container: 'flex-1 px-0 sm:px-2 md:px-8 pt-1 pb-2 sm:pt-2 sm:pb-3 md:pt-2 md:pb-4',
    header: {
      container: 'mb-8',
      title: 'text-4xl font-bold text-title mb-4',
      description: 'text-lg theme-text-secondary',
    },
    content: {
      container: 'theme-bg-secondary rounded-xl p-6',
      section: 'mt-6',
    },
    filterBar: {
      container: 'flex items-center justify-between flex-wrap gap-4',
      title: 'text-lg font-semibold text-title',
      stats: 'text-sm theme-text-secondary',
      controls: 'flex items-center space-x-4',
    },
  },

  // Screenshot placeholder styles
  screenshot: {
    container: 'rounded-lg overflow-hidden',
    header: 'px-4 py-2 text-sm font-medium border-b',
    content: 'p-4 text-center',
    image: 'w-full rounded-lg border shadow-sm',
    placeholder: {
      container: 'w-full h-48 border-2 border-dashed rounded-lg flex items-center justify-center',
      icon: 'text-4xl mb-2',
      text: 'text-sm',
    },
    description: 'text-xs mt-2',
  },
  
  // Tree view styles
  tree: {
    container: 'bg-white rounded-lg overflow-hidden',
    header: {
      container: 'p-4 flex items-center justify-between',
      title: 'font-semibold text-lg text-gray-900',
      controls: 'flex gap-2',
      button: {
        expand: 'px-2 py-1 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-200',
        collapse: 'px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200',
      },
    },
    content: 'p-4',
    node: {
      container: 'select-none',
      item: 'flex items-center py-2 px-2 rounded-md transition-colors',
      itemHover: 'hover:bg-blue-50 cursor-pointer',
      itemClickable: 'cursor-pointer hover:bg-blue-50',
      itemNormal: 'cursor-pointer hover:bg-gray-50',
      chevron: {
        button: 'mr-2 p-0.5 hover:bg-gray-200 rounded transition-colors',
        icon: 'w-4 h-4 text-gray-500',
        spacing: 'mr-2 p-0.5',
      },
      icon: {
        container: 'mr-2 flex items-center',
        text: 'text-lg',
      },
      label: {
        base: 'font-medium text-gray-900',
        clickable: 'font-medium text-gray-900 hover:text-blue-600',
      },
      count: 'ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs',
      children: 'transition-all duration-200 ease-in-out',
    },
    machine: {
      container: 'flex items-center py-2 px-2 hover:bg-blue-50 cursor-pointer rounded-md transition-colors',
      icon: 'mr-2',
      iconBlue: 'text-blue-500',
      iconGray: 'text-gray-600',
      details: 'flex-1',
      name: 'font-medium text-gray-900 hover:text-blue-600',
      id: 'ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs',
      status: {
        available: 'ml-2 px-2 py-1 rounded-full text-xs font-medium text-green-600 bg-green-100',
        unavailable: 'ml-2 px-2 py-1 rounded-full text-xs font-medium text-gray-600 bg-gray-100',
      },
    },
    emptyState: {
      container: 'text-center py-12 text-gray-500',
      icon: 'text-6xl mb-4',
      title: 'text-xl font-semibold mb-2',
      description: 'text-sm',
    },
  },
  
  // Modal styles
  modal: {
    overlay: 'fixed inset-0 z-50 flex items-center justify-center bg-black/30 dark:bg-black/50',
    container: 'bg-white dark:bg-theme-bg-secondary rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-200 dark:border-theme-border-primary',
    header: {
      title: 'text-2xl font-bold text-title dark:text-theme-text-primary mb-4',
    },
    form: {
      label: 'block text-lg font-medium text-gray-700 dark:text-theme-text-primary mb-2',
      input: 'w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-theme-border-primary dark:bg-theme-bg-primary dark:text-theme-text-primary focus:ring-2 focus:ring-cockpitblue dark:focus:ring-blue-400 focus:border-cockpitblue dark:focus:border-blue-400 text-xl mb-2',
      error: 'text-red-500 dark:text-red-400 text-sm mb-2',
    },
    actions: {
      container: 'flex justify-end gap-3 mt-4',
      cancel: 'px-5 py-2 rounded-lg bg-gray-100 dark:bg-theme-bg-primary text-gray-600 dark:text-theme-text-secondary hover:bg-gray-200 dark:hover:bg-theme-interactive-hover font-semibold text-lg',
      submit: 'px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 font-semibold text-lg shadow',
    },
  },
  
  // Sortable tabs (modern pill/segmented bar — responsive, animated)
  sortableTabs: {
    container:
      'relative flex items-center gap-1.5 sm:gap-2 p-1.5 rounded-full bg-gray-100 overflow-x-auto no-scrollbar shadow-[0_2px_14px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_14px_rgba(0,0,0,0.35)] ring-1 ring-black/5 dark:ring-white/10',
    tab: {
      base: 'group relative flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-semibold whitespace-nowrap transition-all duration-200 ease-out select-none focus:outline-none',
      active: 'text-on-brand',
      inactive:
        'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white hover:bg-white/60 dark:hover:bg-white/10',
      corners: '',
      lastTab: '',
      dragging: 'opacity-80 scale-[1.03] shadow-md',
      indicator:
        'absolute left-3.5 right-3.5 sm:left-5 sm:right-5 -bottom-0.5 h-0.5 rounded-full bg-blue-500 dark:bg-blue-400 transition-all duration-200',
    },
    tabContent: {
      container: 'flex items-center justify-center gap-2',
      closeButton: {
        container: 'flex items-center',
        button:
          'w-5 h-5 flex items-center justify-center rounded-full hover:bg-red-500/20 hover:text-red-600 dark:hover:text-red-400 text-gray-400 dark:text-gray-500 transition-colors duration-150 focus:outline-none',
        icon: 'w-3.5 h-3.5',
      },
    },
    addButton: {
      button:
        'ml-0.5 w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl text-gray-400 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-white/70 dark:hover:bg-white/10 transition-colors duration-150',
      icon: 'w-5 h-5',
    },
    content: {
      container:
        'mt-2 bg-gray-100 dark:bg-theme-bg-secondary rounded-3xl px-1 pb-2 sm:p-4 min-h-[200px] relative shadow-[0_6px_28px_rgba(0,0,0,0.07)] dark:shadow-[0_6px_28px_rgba(0,0,0,0.4)] ring-1 ring-black/5 dark:ring-white/10',
    },
  },
  
  // Dashboard options panel
  dashboard: {
    options: {
      container: 'text-center py-16',
      title: 'text-2xl font-bold text-gray-700 mb-6',
      description: 'text-lg text-gray-500 mb-8',
      comingSoon: 'text-sm text-gray-400 italic',
      buttonGrid: 'grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 max-w-4xl mx-auto',
    },
  },
  
  // Compare machine view styles
  compare: {
    container: 'space-y-6',
    layout: {
      container: 'p-8 max-w-7xl mx-auto',
      title: 'text-2xl font-bold mb-6 text-gray-900 dark:text-theme-text-primary',
    },
    loading: 'p-8 text-center text-lg text-gray-500 dark:text-theme-text-secondary',
    error: 'p-8 text-center text-lg text-red-500 dark:text-red-400',
    section: {
      container: 'mb-4',
    },
    header: {
      container: 'space-y-4',
      title: 'font-semibold mb-2 text-gray-700 dark:text-theme-text-secondary',
      selector: 'min-w-[300px] max-w-xl',
    },
    tabs: {
      container: 'flex border-b border-gray-200 dark:border-theme-border-primary mb-6 mt-8',
      tab: {
        base: 'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
        active: 'text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400',
        inactive: 'text-gray-500 dark:text-gray-300 border-transparent hover:text-gray-700 dark:hover:text-gray-100 hover:border-gray-300 dark:hover:border-gray-500',
      },
    },
    info: {
      container: 'text-sm text-gray-600 dark:text-theme-text-secondary bg-blue-50 dark:bg-theme-bg-primary border border-blue-200 dark:border-theme-border-primary rounded-lg p-3',
      title: 'font-medium text-gray-900 dark:text-theme-text-primary',
      subtitle: 'block mt-1',
    },
    machineSection: {
      container: 'bg-white dark:bg-theme-bg-secondary rounded-xl shadow p-6',
      title: 'font-semibold mb-4 text-lg border-b border-gray-200 dark:border-theme-border-primary pb-2 text-gray-900 dark:text-theme-text-primary',
      error: 'text-red-500 dark:text-red-400 italic text-sm',
      noData: 'text-gray-400 dark:text-gray-500 italic text-sm',
    },
    emptyState: 'mt-8 text-gray-500 dark:text-theme-text-secondary',
  },
  
  // Line view styles
  line: {
    info: {
      container: 'bg-white rounded-lg shadow-md p-6 mb-6',
      header: 'flex items-center justify-between mb-4',
      title: 'text-2xl font-bold text-gray-900',
      details: 'text-sm text-gray-600 space-y-1',
      label: 'font-medium',
    },
    machines: {
      container: 'mt-8',
      title: 'text-xl font-semibold mb-4',
      emptyState: {
        container: 'text-center py-12 text-gray-500',
        icon: 'text-6xl mb-4',
        description: 'text-sm',
      },
    },
  },
  
  // Recently viewed tab styles
  recentlyViewedTab: {
    container: 'space-y-4',
    loading: {
      container: 'flex justify-center items-center py-12',
      spinner: 'animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500',
      text: 'ml-3 text-gray-600',
    },
    emptyState: {
      container: 'text-center py-12 text-gray-500',
      icon: 'text-6xl mb-4',
      title: 'text-xl font-semibold mb-2',
      description: 'text-sm',
    },
    header: {
      container: 'flex items-center justify-between',
      title: 'text-lg font-semibold text-title',
      refreshButton: {
        base: 'flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors',
        normal: 'bg-gray-100 hover:bg-gray-200',
        disabled: 'bg-gray-50 text-gray-400',
      },
      refreshIcon: 'w-4 h-4',
      refreshIconLoading: 'w-4 h-4 animate-spin',
    },
    grid: 'grid gap-4 md:grid-cols-2 lg:grid-cols-3',
    cardWrapper: 'relative',
    fallbackCard: {
      container: 'bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer border border-gray-200',
      content: 'flex items-center space-x-3',
      indicator: 'w-3 h-3 rounded-full',
      machineIndicator: 'w-3 h-3 rounded-full bg-blue-500',
      lineIndicator: 'w-3 h-3 rounded-full bg-green-500',
      details: 'flex-1',
      name: 'font-medium text-gray-900 text-sm',
      metadata: 'text-xs text-gray-500 flex items-center',
      metadataIcon: 'mr-2',
      metadataId: 'mr-2',
      metadataSeparator: 'text-gray-400',
      metadataTime: 'ml-2',
    },
  },

  // Lazy machine grid styles
  lazyMachineGrid: {
    container: 'space-y-6',
    emptyState: {
      container: 'text-center py-12 theme-text-muted',
      icon: 'text-6xl mb-4',
      title: 'text-xl font-semibold mb-2 theme-text-primary',
      description: 'text-sm theme-text-secondary',
    },
    summary: {
      container: 'flex items-center justify-between text-sm theme-text-secondary',
      badge: 'text-xs theme-bg-info-subtle theme-text-info px-2 py-1 rounded',
    },
    grid: 'grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    loadMore: {
      container: 'text-center py-8',
      loading: {
        container: 'flex items-center justify-center space-x-2',
        spinner: 'animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full',
        text: 'theme-text-secondary',
      },
      manual: {
        container: 'space-y-3',
        button: 'px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors',
        hint: 'text-xs theme-text-muted',
      },
    },
    performance: {
      info: 'text-xs theme-text-muted text-center mt-4 theme-bg-success-subtle p-3 rounded-lg',
    },
  },

  // Toggle switch styles
  toggle: {
    container: 'flex items-center space-x-3',
    label: 'text-sm theme-text-secondary',
    switch: {
      base: 'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
      active: 'bg-blue-600',
      inactive: 'bg-gray-200',
      thumb: {
        base: 'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
        active: 'translate-x-6',
        inactive: 'translate-x-1',
      },
    },
  },

  // Target management component styles
  targetManagement: {
    container: 'bg-white rounded-lg shadow-md p-6',
    header: {
      container: 'mb-6',
      title: 'text-2xl font-bold text-gray-900 mb-2',
      description: 'text-gray-600',
    },
    loading: {
      container: 'flex items-center justify-center py-8',
      wrapper: 'text-center',
      spinner: 'animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4',
      text: 'text-gray-600',
    },
    alerts: {
      error: {
        base: 'mb-4 p-4 border rounded-md',
        warning: 'bg-yellow-50 border-yellow-200',
        danger: 'bg-red-50 border-red-200',
        title: {
          warning: 'font-medium text-yellow-800',
          danger: 'font-medium text-red-800',
        },
        message: {
          warning: 'text-sm text-yellow-600',
          danger: 'text-sm text-red-600',
        },
        hint: 'text-xs text-yellow-500 mt-1',
      },
      success: {
        container: 'mb-4 p-4 bg-green-50 border border-green-200 rounded-md',
        title: 'text-green-800 font-medium',
        message: 'text-green-600 text-sm',
      },
    },
    layout: {
      grid: 'grid grid-cols-1 lg:grid-cols-2 gap-8',
      leftPanel: 'space-y-6',
      rightPanel: 'space-y-6',
    },
    form: {
      sectionTitle: 'text-lg font-semibold text-gray-900',
      fieldGroup: 'space-y-6',
      field: {
        container: '',
        label: 'block text-sm font-medium text-gray-700 mb-2',
        input: 'w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent',
        select: 'w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent',
        helpText: 'text-sm text-gray-500 mt-1',
      },
      buttons: {
        container: 'flex space-x-3',
        primary: 'flex-1 bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium',
        danger: 'px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium',
      },
    },
    targetOverview: {
      emptyState: {
        container: 'text-center py-8 text-gray-500',
        icon: 'text-4xl mb-2',
        title: 'font-medium',
        description: 'text-sm',
      },
      list: {
        container: 'space-y-4',
        item: {
          base: 'p-4 border rounded-md cursor-pointer transition-colors',
          selected: 'border-blue-500 bg-blue-50',
          default: 'border-gray-200 hover:border-gray-300',
          title: 'font-medium text-gray-900 mb-2',
          content: 'space-y-1 text-sm',
          row: 'flex justify-between',
          label: 'text-gray-600',
          value: 'font-medium',
          timestamp: 'text-xs text-gray-500 mt-2',
        },
      },
    },
    syncInfo: {
      container: 'bg-gray-50 p-4 rounded-md',
      title: 'font-medium text-gray-900 mb-2',
      content: 'text-sm text-gray-600 space-y-1',
      item: '',
    },
  },

  // Overview component styles
  overview: {
    container: 'flex-1 px-0 sm:px-2 md:px-8 pt-0 pb-4 bg-gray-100 dark:bg-theme-bg-secondary sm:rounded-lg', // updated to use gray-100 in light mode
    shiftSelector: {
      container: 'mb-4 flex justify-end',
      wrapper: 'flex-shrink-0',
    },
    tabs: {
      container: 'mt-3 sm:mt-6',
    },
    content: {
      container: 'mt-3 sm:mt-6 theme-bg-surface sm:rounded-xl px-1 pb-2 sm:p-4', // responsive padding
    },
  },

  // Tab component styles
  tabComponents: {
    container: 'space-y-6',
    card: 'theme-bg-surface rounded-lg p-2 sm:p-4 lg:p-6', // removed shadow-sm
    chartCard: 'theme-bg-surface sm:rounded-xl px-1 pb-2 sm:p-4 lg:p-6', // no shadow
    
    // Sub-tab navigation (flat style with underline)
    subTabNav: {
      container: 'flex mb-6', // removed border-b theme-border-primary 
      tab: {
        base: 'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
        active: 'theme-text-accent theme-border-accent', // replaced text-blue-600 border-blue-600
        inactive: 'theme-text-secondary border-transparent hover:theme-text-primary hover:theme-border-secondary', // replaced text-gray-500 hover:text-gray-700 hover:border-gray-300
      },
    },
    
    // Toggle group for aggregation type
    toggleGroup: {
      container: 'flex justify-end mb-6',
      wrapper: 'flex rounded-lg overflow-hidden', // removed theme-border-primary
      button: {
        base: 'px-4 py-2 text-sm font-medium transition-colors',
        active: 'theme-bg-accent theme-text-on-accent', // replaced bg-blue-600 text-white
        inactive: 'theme-bg-surface theme-text-primary hover:theme-bg-hover', // replaced bg-white text-gray-700 hover:bg-gray-50
      },
    },
  },

  // Help page styles
  help: {
    layout: {
      container: 'min-h-screen flex',
      sidebar: 'w-80 shadow-lg flex-shrink-0',
      content: 'flex-1 overflow-y-auto',
      contentInner: 'max-w-full mx-auto px-8 py-8',
    },
    sidebar: {
      container: 'p-6 border-r h-full sticky top-0',
      title: 'text-xl font-bold text-title mb-6',
      nav: 'space-y-1',
      navItem: {
        base: 'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
        active: 'border-r-2 border-blue-700',
        inactive: 'hover:text-gray-900',
        icon: 'mr-3 w-4 h-4',
      },
    },
    content: {
      container: 'space-y-8',
      section: 'rounded-lg shadow-sm p-8 border',
      pageTitle: 'text-3xl font-bold mb-4',
      sectionTitle: 'text-2xl font-semibold mb-4 border-b pb-2',
      paragraph: 'leading-relaxed',
      paragraphWithMargin: 'leading-relaxed mb-6',
      tableOfContents: 'list-decimal list-inside space-y-2',
      divider: 'border-t my-8',
      quickStartGrid: 'grid grid-cols-1 md:grid-cols-2 gap-6 mt-6',
      quickStartCard: 'border rounded-lg p-6',
      quickStartCardTitle: 'text-lg font-semibold mb-3',
      quickStartCardDescription: 'mb-4',
      quickStartCardSteps: 'list-decimal list-inside space-y-2',
      tocLink: 'text-blue-600 hover:text-blue-800 hover:underline',
      threeColumnGrid: 'grid grid-cols-1 md:grid-cols-3 gap-6',
    },
    sections: {
      quickStart: {
        container: 'border rounded-lg p-6 mb-8',
        title: 'text-xl font-bold mb-4',
        description: 'mb-4',
        grid: 'grid grid-cols-1 md:grid-cols-2 gap-6',
        card: 'border rounded-lg p-6 shadow-sm',
        cardTitle: 'text-lg font-semibold mb-3',
        cardDescription: 'mb-4',
        cardSteps: 'list-decimal list-inside space-y-2 text-sm',
      },
      blue: 'border rounded-lg p-6 mb-8',
      purple: 'border rounded-lg p-6 mb-8',
      yellow: 'border rounded-lg p-6 mb-8',
      red: 'border rounded-lg p-6 mb-8',
    },
    infoBox: {
      container: 'border rounded-lg p-6 mb-6',
      title: 'font-semibold mb-3',
      titleWithIcon: 'font-semibold mb-3 text-lg',
      subtitle: 'font-medium mb-2',
      content: '',
      highlight: 'border rounded-lg p-4 my-4',
      highlighted: 'border rounded-lg p-4 my-4',
      highlightTitle: 'font-semibold mb-2',
      highlightContent: '',
    },
    statusColors: {
      container: 'space-y-4 my-6',
      item: 'flex items-center gap-3 p-3 rounded-md',
      indicator: 'w-4 h-4 rounded-full flex-shrink-0',
      text: 'text-sm',
      success: 'border',
      error: 'border',
      warning: 'border',
      info: 'border',
      purple: 'border',
      orange: 'border',
    },
    faq: {
      container: 'space-y-6',
      item: 'border rounded-lg p-6',
      question: 'font-semibold mb-3 text-lg',
      answer: 'leading-relaxed',
      category: 'text-xs uppercase tracking-wide font-medium mb-2',
    },
    kpiCards: {
      container: 'grid grid-cols-1 md:grid-cols-3 gap-6 my-8',
      card: 'border rounded-lg p-6 shadow-sm',
      title: 'text-lg font-semibold mb-4',
      formula: 'border rounded-lg p-4 font-mono text-sm mb-4',
      description: 'text-sm leading-relaxed',
      example: 'border rounded-lg p-3 mt-3',
      exampleTitle: 'font-medium mb-1 text-sm',
      exampleContent: 'text-sm',
    },
    kpi: {
      grid: 'grid grid-cols-1 lg:grid-cols-2 gap-8 my-6 max-w-none',
      card: 'border rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-blue-300 min-h-[180px] w-full',
      header: {
        container: 'flex items-start justify-between mb-4',
        titleContainer: 'flex-1',
        title: 'text-xl font-bold mb-2 leading-tight',
        category: 'text-sm uppercase tracking-wide text-blue-600 font-semibold mb-2',
        indicator: 'w-4 h-4 rounded-full flex-shrink-0 mt-1 ml-4',
      },
      formula: {
        container: 'border rounded-xl p-6 mb-4 overflow-x-auto min-h-[80px] flex items-center justify-center',
        math: 'text-center font-mono text-lg w-full px-4',
        label: 'text-xs uppercase tracking-wide font-semibold mb-3 text-center',
      },
      description: {
        container: 'grid grid-cols-1 lg:grid-cols-2 gap-6 items-start',
        text: 'leading-relaxed text-sm',
        highlight: 'border-l-4 border-blue-400 p-4 rounded-r-lg',
        highlightText: 'text-sm font-medium leading-relaxed',
      },
      badge: {
        container: 'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mt-4',
        icon: 'w-3 h-3',
      },
    },
    lists: {
      simple: 'list-disc list-inside space-y-1 ml-4',
      bulleted: 'list-disc list-inside space-y-2 ml-4',
    },
    whatsNew: {
      container: 'space-y-6 mt-8',
      version: 'border rounded-lg p-6',
      versionTitle: 'text-xl font-semibold mb-2',
      versionDate: 'text-sm mb-4',
    },
    contact: {
      container: 'border rounded-lg p-6 mt-8',
      title: 'text-xl font-semibold mb-4',
      grid: 'grid grid-cols-1 md:grid-cols-2 gap-6',
      item: 'space-y-2',
      itemTitle: 'font-medium',
      itemContent: '',
      link: 'text-blue-600 hover:text-blue-800 hover:underline',
      needMoreHelp: 'text-center mt-6 p-4 rounded-lg',
      technicalSupport: 'space-y-4',
      servicePortal: 'space-y-4',
      sectionTitle: 'text-lg font-semibold mb-3',
      contactInfo: 'space-y-2',
      label: 'font-medium',
      email: 'text-blue-600 hover:text-blue-800',
      description: 'mb-4',
      button: 'inline-flex items-center px-4 py-2 rounded-md transition-colors',
    },
  },

  // Chart component styles
  chart: {
    container: 'w-full',
    emptyState: {
      container: 'flex items-center justify-center h-96',
      wrapper: 'text-center',
      icon: 'text-gray-400 text-6xl mb-4',
      title: 'text-lg font-semibold text-gray-900 mb-2',
      description: 'text-gray-600',
    },
  },

  // Globe component styles
  globe: {
    container: 'space-y-6',
    wrapper: 'theme-bg-surface rounded-lg shadow-md overflow-hidden border theme-border-primary',
    header: {
      container: 'p-4 border-b theme-border-primary',
      content: 'flex items-center justify-between',
      title: 'text-lg font-semibold text-title',
      description: 'text-sm theme-text-secondary mt-1',
      button: 'px-4 py-2 theme-bg-tertiary hover:theme-bg-secondary rounded-md transition-colors text-sm font-medium theme-text-primary',
    },
    canvas: {
      container: 'relative',
      height: 'h-[500px]',
    },
    legend: {
      container: 'absolute bottom-4 left-4 bg-black/80 text-white p-4 rounded-lg text-sm backdrop-blur-sm border border-white/10',
      title: 'font-semibold mb-2 text-white',
      list: 'space-y-1',
      item: 'flex items-center gap-2',
      indicator: 'w-3 h-3 rounded-full',
      text: 'text-sm text-white',
      footer: 'flex items-center gap-2 mt-2 pt-2 border-t border-white/20',
      footerText: 'text-xs opacity-75 text-white/75',
    },
    overlay: {
      container: 'absolute top-4 right-4 theme-bg-surface backdrop-blur-sm p-4 rounded-lg shadow-lg border theme-border-primary max-w-xs',
      title: 'font-semibold theme-text-primary mb-2',
      content: 'text-sm theme-text-secondary space-y-1',
      row: 'space-y-1',
      label: 'font-medium',
      value: '',
      footer: 'text-xs theme-text-muted mt-2',
    },
    machineGrid: {
      container: 'theme-bg-surface rounded-lg shadow-md p-6 border theme-border-primary',
      header: {
        container: 'flex items-center justify-between mb-4',
        title: 'text-lg font-semibold text-title',
        subtitle: 'text-sm theme-text-secondary',
      },
    },
    summary: {
      container: 'theme-bg-surface rounded-lg shadow-md p-6 border theme-border-primary',
      title: 'text-lg font-semibold text-title mb-4',
      grid: 'grid grid-cols-2 md:grid-cols-4 gap-4',
      stat: {
        container: 'text-center',
        value: 'text-2xl font-bold theme-text-primary',
        label: 'text-sm theme-text-secondary',
        blue: 'text-blue-600',
        green: 'text-green-600',
        orange: 'text-orange-600',
        purple: 'text-purple-600',
      },
    },
  },

  // App component styles
  app: {
    container: 'min-h-screen bg-cockpitbg flex flex-col',
    modal: {
      overlay: 'fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm',
      container: 'relative bg-white rounded-2xl shadow-2xl w-full max-w-[950px] min-w-[700px] flex flex-col overflow-visible border border-gray-200 animate-fadeIn',
    },
  },

  // Auth loading styles
  auth: {
    loading: {
      container: 'flex items-center justify-center min-h-screen',
      wrapper: 'text-center',
      spinner: 'animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600',
      text: 'mt-4 text-gray-600',
    },
    authUnavailable: {
      container: 'flex items-center justify-center min-h-screen',
      wrapper: 'text-center max-w-md mx-auto p-6',
      errorIcon: {
        container: 'text-red-600 mb-4',
        svg: 'h-16 w-16 mx-auto',
      },
      title: 'text-xl font-semibold text-gray-900 mb-2',
      description: 'text-gray-600 mb-4',
      notice: 'text-sm text-gray-500 mb-4',
      retryButton: 'px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors',
    },
    debugInfo: {
      container: 'mt-4 p-4 bg-blue-50 border border-blue-200 rounded max-w-md mx-auto',
      text: 'text-sm text-blue-800',
    },
  },

  // Line view additional styles  
  lineView: {
    statsGrid: 'grid grid-cols-1 md:grid-cols-3 gap-6 mb-8',
    statCard: {
      container: 'theme-bg-surface rounded-lg p-4 shadow-sm',
      title: 'text-sm font-semibold theme-text-secondary uppercase tracking-wide',
      value: 'text-2xl font-bold theme-text-primary mt-2',
    },
    detailsGrid: 'grid grid-cols-1 md:grid-cols-2 gap-4',
  },
});

/**
 * Conditionally concatenate class names: the shared helper of
 * @digitalisation/enlixy-comp (falsy values dropped, joined with single
 * spaces), re-exported so `import { cx } from '../styles'` keeps working.
 */
export { cx } from '@digitalisation/enlixy-comp';

/**
 * Get status color based on condition or status type
 */
export const getStatusColor = (condition: boolean | 'success' | 'error' | 'warning' | 'info' | 'disabled'): string => {
  const statusStyles = {
    success: `w-3 h-3 rounded-full flex-shrink-0 bg-green-500`,
    error: `w-3 h-3 rounded-full flex-shrink-0 bg-red-500`,
    warning: `w-3 h-3 rounded-full flex-shrink-0 bg-yellow-500`,
    disabled: `w-3 h-3 rounded-full flex-shrink-0 bg-gray-400`,
    info: `w-3 h-3 rounded-full flex-shrink-0 bg-blue-500`,
  };
  
  if (typeof condition === 'boolean') {
    return condition ? statusStyles.success : statusStyles.error;
  }
  return statusStyles[condition] || statusStyles.disabled;
};

// Default styles export for components that expect it
export const styles = {
  ...createThemeAwareStyles({
    bgSecondary: 'theme-bg-secondary',
    borderPrimary: 'theme-border-primary',
    textSecondary: 'theme-text-secondary',
    hover: 'theme-hover',
  }),
  utility: {
    spacing: {
      xl: 'px-4 md:px-8 py-6',
    },
    flex: {
      end: 'flex justify-end',
      between: 'flex justify-between',
    },
  },
  card: {
    chart: 'theme-bg-surface rounded-xl p-1 sm:p-4',
  },
  tabs: {
    subTabs: {
      container: 'flex mb-6',
      tab: 'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
      active: 'theme-text-accent border-blue-600',
      inactive: 'theme-text-secondary border-transparent hover:theme-text-primary hover:border-gray-300',
    },
  },
  tabComponents: {
    subTabNav: {
      container: 'flex mb-6',
      tab: {
        base: 'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
        active: 'theme-text-accent border-blue-600',
        inactive: 'theme-text-secondary border-transparent hover:theme-text-primary hover:border-gray-300',
      },
    },
    toggleGroup: {
      container: 'flex justify-end mb-6',
      wrapper: 'flex rounded-lg overflow-hidden',
      button: {
        base: 'px-4 py-2 text-sm font-medium transition-colors',
        active: 'bg-blue-600 text-white',
        inactive: 'theme-bg-surface theme-text-primary hover:theme-bg-hover',
      },
    },
  },
};
